import { Button, ButtonProps, Default, Playback } from '@amzn/dvui/dist/Button'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import * as React from 'react'
import * as st from './PlayButton.module.scss'
import * as cn from 'classnames'
import { AtfTreatmentProps } from '../../reducers/features'
import { isTreatment } from '../../utils/utils'

export interface PlayButtonProps extends ButtonProps, AtfTreatmentProps {
  isSeeThrough?: boolean
  isPackshotButton?: boolean
  ariaLabel?: string
}

export const PlayButton: React.SFC<PlayButtonProps> = ({
  isSeeThrough = false,
  isPackshotButton = false,
  children,
  ariaLabel,
  onClick,
  atfTreatment,
  className,
  href,
}) => {
  const playButtonClass = cn(
    'dvui-playButton',
    st.circle,
    isSeeThrough ? st.seeThrough : st.opaque,
    isPackshotButton ? st.packshotButton : '',
    className,
    {[st.showOnlyInCollapsed]: isTreatment(atfTreatment, 'T2')}
  )

  return (
    <a
      href={href}
      onClick={onClick as any}
      aria-label={ariaLabel}
      className={
        isTreatment(atfTreatment,'T3')
          ? st.clickArea
          : playButtonClass
      }>
      {!isTreatment(atfTreatment, 'T3') && (
        <>
          <Icon use={IconType.PLAY_BUTTON_ARROW} className={st.icon} />
          {children && (
            <span className='avu-screen-reader-only'>{children}</span>
          )}
        </>
      )}
    </a>
  )
}

export interface PlayBoxProps extends ButtonProps {
  progress?: number
  isLarge?: boolean
  isStartOver?: boolean
  ariaLabel?: string
}

export const PlayBox: React.SFC<PlayBoxProps> = ({
  progress = 0,
  isLarge = false,
  isStartOver = false,
  ariaLabel,
  onClick,
  href,
  className,
  children,
}) => {
  const playButtonClass = cn(
    st.playbox,
    { [st.largePlaybox]: isLarge === true },
    isLarge ? st.largePlaybox : st.smallPlaybox,
    className
  )

  return (
    <a
      href={href}
      onClick={onClick as any}
      aria-label={ariaLabel}
      className={playButtonClass}>
      <span className={`${st.circle} ${st.opaque}`}>
        <Icon
          use={isStartOver ? IconType.START_OVER : IconType.PLAY_BUTTON_ARROW}
          className={cn(st.icon, { [st.startOverIcon]: isStartOver })}
        />
      </span>
      <span className={st.playButtonRightContent}>
        {children}
        <ProgressBar progress={progress} />
      </span>
    </a>
  )
}
PlayBox.displayName = 'PlayButton'

const ProgressBar: React.SFC<{ progress?: number }> = ({ progress = 0 }) =>
  progress > 0 ? (
    <span className={st.progress}>
      <span
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={progress}
        className={st.progressBar}
        role='progressbar'
        style={{ width: `${progress}%` }}
      />
    </span>
  ) : null

export interface SquarePlayButtonProps extends ButtonProps, AtfTreatmentProps {
  progress?: number
  isStartOver?: boolean
}

export const SquarePlayButton: React.SFC<SquarePlayButtonProps> = ({
  progress = 0,
  isStartOver,
  atfTreatment,
  className,
  children,
  ...buttonProps
}) => (
  <Button
    {...buttonProps}
    use={isStartOver ? Default : Playback}
    useIcon={isStartOver ? IconType.START_OVER : IconType.PLAY_ARROW}
    className={`${st.squareButton} ${className}`}>
    <span>{children}</span>
    <ProgressBar progress={progress} />
  </Button>
)

SquarePlayButton.displayName = 'SquarePlayButton'

export const TrailerButton: React.SFC<ButtonProps> = (buttonProps) => (
  <Button {...buttonProps} useIcon={IconType.TRAILER} />
)
